interface CookieOptions {
  /** Lifetime in days. Defaults to `7`. */
  days?: number;
  /** Cookie path scope. Must start with `/`. Defaults to `"/"`. */
  path?: string;
  /** Domain scope — bare hostname only (e.g. `".straumann.com"`). No protocol or path. */
  domain?: string;
  /** SameSite attribute per RFC 6265bis. Defaults to `"Lax"`. */
  sameSite?: "Strict" | "Lax" | "None";
  /** Mark as Secure (HTTPS-only). Required when `sameSite` is `"None"`. */
  secure?: boolean;
}

/**
 * Native cookie utility – lightweight replacement for js-cookie.
 *
 * Provides get / set / remove / has helpers that cover every usage pattern
 * found in the codebase (plain values, JSON round-trips, domain-scoped
 * cookies, expiration in days, etc.).
 */
export const cookies = {
  /**
   * Set a cookie.
   * @param name  - Cookie name.
   * @param value - Cookie value (will be URI-encoded).
   * @param options - Cookie attributes (days, path, domain, etc.).
   */
  set(name: string, value: string, { days = 7, path = "/", domain, sameSite = "Lax", secure }: CookieOptions = {}): void {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();

    let cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=${path}; SameSite=${sameSite}`;

    if (domain) {
      cookie += `; domain=${domain}`;
    }
    if (secure) {
      cookie += "; Secure";
    }

    document.cookie = cookie;
  },

  /**
   * Read a cookie value by name.
   * @param name - Cookie name.
   * @returns Decoded value, or `undefined` when absent.
   */
  get(name: string): string | undefined {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : undefined;
  },

  /**
   * Delete a cookie.
   * @param name - Cookie name.
   * @param options - Path and domain must match the original cookie to delete it.
   */
  remove(name: string, { path = "/", domain }: Pick<CookieOptions, "path" | "domain"> = {}): void {
    let cookie = `${name}=; max-age=0; path=${path}`;

    if (domain) {
      cookie += `; domain=${domain}`;
    }

    document.cookie = cookie;
  },

  /** Check whether a cookie exists. */
  has(name: string): boolean {
    return this.get(name) !== undefined;
  },
};
